import React, { useState, useEffect } from 'react';
import { TrendingDown, TrendingUp, AlertTriangle, CheckCircle, HelpCircle, Flame, Award, PlusCircle } from 'lucide-react';
import StatCard from '../shared/StatCard';
import { detectSlump, detectHittingStreak } from '../../utils/advancedAnalytics';
import { usePlayer } from '../../context/PlayerContext';
import { trackEvent, AnalyticsEvents } from '../../utils/analytics';

const SlumpDetection = ({ className }) => {
  const { playerData, activeSeason, viewingAllSeasons } = usePlayer();
  const [slumpAnalysis, setSlumpAnalysis] = useState(null);
  const [streakAnalysis, setStreakAnalysis] = useState(null);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    // Reset state when season changes
    setSlumpAnalysis(null);
    setStreakAnalysis(null);
    
    // Get the appropriate stats based on whether we're viewing season or career stats
    const battingStats = !viewingAllSeasons && activeSeason && playerData?.seasonStats?.battingStats
      ? playerData.seasonStats.battingStats
      : playerData?.battingStats;
      
    const pitchingStats = !viewingAllSeasons && activeSeason && playerData?.seasonStats?.pitchingStats
      ? playerData.seasonStats.pitchingStats
      : playerData?.pitchingStats;
    
    // Check if we have enough games to analyze
    const hasEnoughGames = battingStats && battingStats.games >= 3;
    
    // In a real implementation, we would use actual game-by-game data
    // For demonstration, we'll generate mock game data based on the selected season
    const generateMockGameStats = () => {
      // If we don't have enough games, return empty array
      if (!hasEnoughGames) {
        return [];
      }
      
      // Create games based on the number of games played in the season
      const numGames = battingStats.games;
      const games = [];
      let currentSlump = false;
      let slumpStartIndex = Math.floor(Math.random() * (numGames / 2)) + Math.floor(numGames / 4); // Start slump somewhere in the middle
      
      // Add a hitting streak at the end (most recent games)
      const hasHittingStreak = Math.random() > 0.3; // 70% chance of having a streak
      const streakLength = hasHittingStreak ? Math.min(Math.floor(Math.random() * 6) + 2, numGames) : 0; // 2-7 games, but not more than total games
      
      // Get season start date or default to current year
      const seasonStartDate = activeSeason?.startDate 
        ? new Date(activeSeason.startDate) 
        : new Date(new Date().getFullYear(), 3, 1); // Default to April 1st of current year
      
      for (let i = 0; i < numGames; i++) {
        // If in slump period (up to 5 games), reduce batting average
        const inSlump = i >= slumpStartIndex && i < Math.min(slumpStartIndex + 5, numGames);
        
        // If in the last few games and we want a hitting streak
        const inStreak = hasHittingStreak && i >= numGames - streakLength;
        
        const atBats = Math.floor(Math.random() * 3) + 2; // 2-4 at bats
        
        // Determine hits based on whether in slump, streak, or normal
        let hits;
        if (inStreak) {
          // During streak, ensure at least 1 hit, with chance of multi-hit game
          hits = Math.random() < 0.6 ? 2 : 1;
        } else if (inSlump) {
          // During slump, lower hit probability
          hits = Math.random() < 0.2 ? 1 : 0;
        } else {
          // Normal performance - use actual batting average to determine hit probability
          const hitProbability = battingStats.average || 0.250;
          hits = Math.random() < hitProbability ? (Math.random() > 0.7 ? 2 : 1) : 0;
        }
        
        // Calculate game date based on season start date
        const gameDate = new Date(seasonStartDate);
        gameDate.setDate(seasonStartDate.getDate() + i * 2); // Assume games every other day
        
        games.push({
          gameId: i + 1,
          date: gameDate.toISOString(),
          opponent: `Team ${i % 10 + 1}`,
          seasonId: activeSeason?.id || 'default',
          atBats,
          hits,
          walks: Math.floor(Math.random() * 2),
          strikeouts: inSlump ? Math.floor(Math.random() * 2) + 1 : Math.floor(Math.random() * 2),
          hardHitBalls: inSlump ? 0 : Math.floor(Math.random() * 2)
        });
      }
      
      return games;
    };
    
    // Generate mock game stats and analyze for slumps and streaks
    const mockGameStats = generateMockGameStats();
    
    // Only analyze if we have enough games
    if (mockGameStats.length >= 3) {
      // Analyze for slumps
      const slumpAnalysis = detectSlump(mockGameStats, {
        slumpThreshold: 0.150,
        hotStreakThreshold: 0.350,
        minGames: 3,
        recentGamesWindow: Math.min(10, mockGameStats.length)
      });
      
      // Analyze for hitting streaks
      const streakAnalysis = detectHittingStreak(mockGameStats, {
        minHitStreak: 2,
        warmStreakThreshold: 3,
        hotStreakThreshold: 5
      });
      
      // Track analytics events for performance analysis
      if (slumpAnalysis.isSlumping) {
        trackEvent(AnalyticsEvents.SLUMP_DETECTED, {
          duration: slumpAnalysis.slumpDuration,
          average: slumpAnalysis.recentAvg,
          seasonId: activeSeason?.id || 'all'
        });
      } else if (slumpAnalysis.isHot) {
        trackEvent(AnalyticsEvents.HOT_STREAK_DETECTED, {
          duration: slumpAnalysis.hotStreakDuration,
          average: slumpAnalysis.recentAvg,
          seasonId: activeSeason?.id || 'all'
        });
      }
      
      if (streakAnalysis.hasHittingStreak) {
        trackEvent(AnalyticsEvents.HITTING_STREAK_DETECTED, {
          length: streakAnalysis.currentStreakLength,
          status: streakAnalysis.streakStatus,
          multiHitGames: streakAnalysis.multiHitGames,
          averageHits: streakAnalysis.averageHitsPerGame,
          seasonId: activeSeason?.id || 'all'
        });
      }
      
      setSlumpAnalysis(slumpAnalysis);
      setStreakAnalysis(streakAnalysis);
    } else {
      // Set empty analysis if not enough games
      setSlumpAnalysis({
        isSlumping: false,
        isHot: false,
        recentAvg: '0.000',
        noData: true
      });
      
      setStreakAnalysis({
        hasHittingStreak: false,
        currentStreakLength: 0,
        noData: true
      });
    }
  }, [playerData, activeSeason, viewingAllSeasons]);
  
  // If analysis is still loading
  if (!slumpAnalysis || !streakAnalysis) {
    return (
      <StatCard className={className}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-medium">Performance Analysis</h3>
        </div>
        <div className="h-24 flex items-center justify-center bg-gray-50 animate-pulse">
          <div className="text-gray-400">Analyzing performance trends...</div>
        </div>
      </StatCard>
    );
  }
  
  // If no data for the selected season
  if (slumpAnalysis.noData) {
    return (
      <StatCard className={className}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-medium">Performance Analysis</h3>
        </div>
        <div className="p-6 text-center">
          <AlertTriangle size={24} className="mx-auto mb-2 text-gray-400" />
          <h4 className="font-medium text-gray-700 dark:text-darkText-primary mb-1">
            No performance data available
          </h4>
          <p className="text-sm text-gray-500 dark:text-darkText-secondary mb-4">
            {activeSeason 
              ? `Add game stats to ${activeSeason.name} to see performance analysis.`
              : 'Select a season with game data to see performance analysis.'}
          </p>
          <button 
            className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => {/* Navigate to add game */}}
          >
            <PlusCircle size={16} className="mr-1" />
            Add Game Stats
          </button>
        </div>
      </StatCard>
    );
  }
  
  // Determine status icon and color based on slump analysis
  const getSlumpStatusDisplay = () => {
    if (slumpAnalysis.isSlumping) {
      return {
        icon: <TrendingDown size={20} className="text-red-500" />,
        color: 'red',
        text: 'Slumping',
        bgClass: 'bg-red-50'
      };
    } else if (slumpAnalysis.isHot) {
      return {
        icon: <TrendingUp size={20} className="text-green-500" />,
        color: 'green',
        text: 'Hot Streak',
        bgClass: 'bg-green-50'
      };
    } else {
      return {
        icon: <HelpCircle size={20} className="text-blue-500" />,
        color: 'blue',
        text: 'Performing as Expected',
        bgClass: 'bg-blue-50'
      };
    }
  };
  
  // Determine hitting streak display
  const getStreakDisplay = () => {
    if (!streakAnalysis.hasHittingStreak) {
      return null;
    }
    
    let icon, color, text, bgClass;
    
    switch (streakAnalysis.streakStatus) {
      case 'hot':
        icon = <Flame size={20} className="text-green-500" />;
        color = 'green';
        text = `${streakAnalysis.currentStreakLength}-Game Hitting Streak`;
        bgClass = 'bg-green-50';
        break;
      case 'warm':
        icon = <Flame size={20} className="text-orange-500" />;
        color = 'orange';
        text = `${streakAnalysis.currentStreakLength}-Game Hitting Streak`;
        bgClass = 'bg-orange-50';
        break;
      case 'mini':
        icon = <Award size={20} className="text-blue-500" />;
        color = 'blue';
        text = `${streakAnalysis.currentStreakLength}-Game Hitting Streak`;
        bgClass = 'bg-blue-50';
        break;
      default:
        return null;
    }
    
    return {
      icon,
      color,
      text,
      bgClass,
      multiHitGames: streakAnalysis.multiHitGames,
      averageHits: streakAnalysis.averageHitsPerGame
    };
  };
  
  const slumpStatusDisplay = getSlumpStatusDisplay();
  const streakDisplay = getStreakDisplay();

  return (
    <StatCard className={className}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium">Performance Analysis</h3>
        <button 
          className="text-blue-600 text-sm"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show Less' : 'Show More'}
        </button>
      </div>
      
      {/* Hitting Streak Section (if exists) */}
      {streakDisplay && (
        <div className={`p-3 rounded-lg ${streakDisplay.bgClass} flex items-center mb-3`}>
          <div className="mr-3">
            {streakDisplay.icon}
          </div>
          <div>
            <div className={`font-bold text-${streakDisplay.color}-700`}>
              {streakDisplay.text}
            </div>
            <div className="text-sm text-gray-600">
              {streakDisplay.multiHitGames > 0 && 
                `${streakDisplay.multiHitGames} multi-hit game${streakDisplay.multiHitGames > 1 ? 's' : ''} • `}
              Avg. {streakDisplay.averageHits} hits per game
            </div>
          </div>
        </div>
      )}
      
      {/* Slump/Hot Streak Section */}
      <div className={`p-3 rounded-lg ${slumpStatusDisplay.bgClass} flex items-center mb-3`}>
        <div className="mr-3">
          {slumpStatusDisplay.icon}
        </div>
        <div>
          <div className={`font-bold text-${slumpStatusDisplay.color}-700`}>
            {slumpStatusDisplay.text}
          </div>
          <div className="text-sm text-gray-600">
            Recent AVG: {slumpAnalysis.recentAvg}
            {slumpAnalysis.isSlumping && ` (${slumpAnalysis.slumpDuration} games)`}
            {slumpAnalysis.isHot && ` (${slumpAnalysis.hotStreakDuration} games)`}
          </div>
        </div>
      </div>
      
      {/* Slump Analysis Details */}
      {slumpAnalysis.isSlumping && (
        <div className="space-y-3">
          {slumpAnalysis.slumpDetails && (
            <div className="border-l-4 border-red-500 pl-3 py-1">
              <div className="font-medium">Slump Analysis</div>
              <div className="flex flex-wrap gap-2 mt-2">
                <div className="bg-gray-100 p-1 rounded text-xs">
                  <span className="font-semibold">K Rate:</span> {slumpAnalysis.slumpDetails.strikeoutRate}
                </div>
                <div className="bg-gray-100 p-1 rounded text-xs">
                  <span className="font-semibold">BB Rate:</span> {slumpAnalysis.slumpDetails.walkRate}
                </div>
                <div className="bg-gray-100 p-1 rounded text-xs">
                  <span className="font-semibold">Hard Hit:</span> {slumpAnalysis.slumpDetails.hardHitRate}
                </div>
              </div>
              
              {expanded && slumpAnalysis.slumpDetails.potentialCauses.length > 0 && (
                <div className="mt-2">
                  <div className="text-sm font-medium">Potential Causes:</div>
                  <ul className="mt-1 space-y-1">
                    {slumpAnalysis.slumpDetails.potentialCauses.map((cause, index) => (
                      <li key={index} className="flex items-start text-xs">
                        <AlertTriangle size={12} className="text-red-500 mr-1 mt-0.5" />
                        <span>{cause.issue} ({cause.factor})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {expanded && slumpAnalysis.recommendations && slumpAnalysis.recommendations.length > 0 && (
            <div>
              <div className="text-sm font-medium mb-2">Recommendations:</div>
              <ul className="space-y-2">
                {slumpAnalysis.recommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* Hot streak content */}
      {slumpAnalysis.isHot && (
        <div className="border-l-4 border-green-500 pl-3 py-1">
          <div className="font-medium">Hot Streak!</div>
          <div className="text-sm mt-1">
            You're on a {slumpAnalysis.hotStreakDuration}-game hot streak with a {slumpAnalysis.recentAvg} average.
          </div>
          
          {expanded && (
            <div className="mt-2 text-sm">
              <div className="font-medium">Key to Success:</div>
              <ul className="mt-1 space-y-1">
                <li className="flex items-start">
                  <CheckCircle size={12} className="text-green-500 mr-1 mt-0.5" />
                  <span>Maintaining consistent swing mechanics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={12} className="text-green-500 mr-1 mt-0.5" />
                  <span>Good pitch selection and patience</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={12} className="text-green-500 mr-1 mt-0.5" />
                  <span>Solid preparation and game planning</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* Normal performance content */}
      {!slumpAnalysis.isSlumping && !slumpAnalysis.isHot && (
        <div className="text-sm">
          <p>Your recent performance is consistent with your overall stats.</p>
          {expanded && (
            <div className="mt-2">
              <div className="font-medium">Focus Areas:</div>
              <ul className="mt-1 space-y-1">
                <li className="flex items-start">
                  <CheckCircle size={12} className="text-blue-500 mr-1 mt-0.5" />
                  <span>Continue working on consistent hitting approach</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={12} className="text-blue-500 mr-1 mt-0.5" />
                  <span>Maintain regular batting practice routine</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </StatCard>
  );
};

export default SlumpDetection;
